import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  Plus, Search, ChevronLeft, Wallet, Eye, Send, Edit, Trash2,
  ArrowUpRight, ArrowDownRight, ArrowLeftRight, CheckCircle, Clock,
  DollarSign, Calendar, Building2, FileText, BarChart3, TrendingUp
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const MODE_CONFIG = {
  original: {
    label: 'Original Budget',
    description: 'Enter initial budget allocations for project WBS elements',
    icon: DollarSign,
    types: ['ORIGINAL'],
    defaultType: 'ORIGINAL',
    color: 'blue'
  },
  change: {
    label: 'Budget Change',
    description: 'Supplement or return budget amounts for existing allocations',
    icon: ArrowUpRight,
    types: ['SUPPLEMENT', 'RETURN'],
    defaultType: 'SUPPLEMENT',
    color: 'amber'
  },
  transfer: {
    label: 'Budget Transfer',
    description: 'Transfer budget between WBS elements within a project',
    icon: ArrowLeftRight,
    types: ['TRANSFER'],
    defaultType: 'TRANSFER',
    color: 'purple'
  }
};

const BudgetManagement = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('budget_management');
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wbsElements, setWbsElements] = useState([]);
  const [wbsBudgets, setWbsBudgets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [budgetMode, setBudgetMode] = useState('original');
  const [filters, setFilters] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    budget_type: ''
  });
  
  // Dialogs
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form
  const [formData, setFormData] = useState({
    project_id: '',
    budget_type: 'ORIGINAL',
    fiscal_year: new Date().getFullYear(),
    posting_date: new Date().toISOString().split('T')[0],
    document_date: new Date().toISOString().split('T')[0],
    total_amount: 0,
    currency: 'USD',
    reason: '',
    header_text: '',
    line_items: []
  });
  
  const [lineItem, setLineItem] = useState({
    wbs_element_id: '',
    amount: 0,
    text: '',
    target_wbs_element_id: ''
  });

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [filters]);

  useEffect(() => {
    // Update filter when mode changes
    const modeTypes = MODE_CONFIG[budgetMode].types;
    setFilters(prev => ({ ...prev, budget_type: '' }));
  }, [budgetMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [docsRes, projectsRes] = await Promise.all([
        psAPI.getBudgetDocuments(filters),
        psAPI.getProjects()
      ]);
      setDocuments(docsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load budget documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchWBSElements = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await psAPI.getWBSElements({ project_id: projectId });
      setWbsElements(response.data.filter(w => w.is_account_assignment_element !== false || w.is_planning_element !== false));
    } catch (error) {
      console.error('Error fetching WBS elements:', error);
    }
  };

  const fetchWBSBudgets = async (projectId, fiscalYear) => {
    if (!projectId) return;
    try {
      const response = await psAPI.getProjectBudgetSummary(projectId, fiscalYear || formData.fiscal_year);
      setWbsBudgets(response.data || []);
    } catch (error) {
      console.error('Error fetching WBS budgets:', error);
      setWbsBudgets([]);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData(prev => ({ ...prev, project_id: projectId, line_items: [] }));
    fetchWBSElements(projectId);
    if (budgetMode === 'change' || budgetMode === 'transfer') {
      fetchWBSBudgets(projectId, formData.fiscal_year);
    }
  };

  const addLineItem = () => {
    if (!lineItem.wbs_element_id || lineItem.amount <= 0) {
      toast.error('Please select WBS element and enter amount');
      return;
    }
    if (formData.budget_type === 'TRANSFER' && !lineItem.target_wbs_element_id) {
      toast.error('Please select target WBS element for transfer');
      return;
    }

    const wbs = wbsElements.find(w => w.wbs_element_id === lineItem.wbs_element_id);
    const newItem = {
      ...lineItem,
      fiscal_year: formData.fiscal_year,
      budget_category: 'COSTS',
      currency: formData.currency,
      wbs_name: wbs?.name || lineItem.wbs_element_id
    };

    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, newItem],
      total_amount: prev.total_amount + lineItem.amount
    }));
    setLineItem({ wbs_element_id: '', amount: 0, text: '', target_wbs_element_id: '' });
  };

  const removeLineItem = (index) => {
    const item = formData.line_items[index];
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
      total_amount: prev.total_amount - item.amount
    }));
  };

  const handleSubmit = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.map(({ wbs_name, ...item }) => item)
      };

      await psAPI.createBudgetDocument(payload);
      toast.success('Budget document created successfully');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'object' ? errorMsg.msg : errorMsg || 'Failed to create document');
    }
  };

  const handleRelease = async (amount = null) => {
    try {
      await psAPI.releaseBudgetDocument(selectedDoc.document_number, amount);
      toast.success('Budget document released successfully');
      setShowReleaseDialog(false);
      setSelectedDoc(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'object' ? errorMsg.msg : errorMsg || 'Failed to release');
    }
  };

  const openViewDialog = async (doc) => {
    try {
      const response = await psAPI.getBudgetDocument(doc.document_number);
      setSelectedDoc(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load document details');
    }
  };

  const openEditDialog = async (doc) => {
    try {
      const response = await psAPI.getBudgetDocument(doc.document_number);
      const docData = response.data;
      setSelectedDoc(docData);
      setIsEditing(true);
      setFormData({
        project_id: docData.project_id,
        budget_type: docData.budget_type,
        fiscal_year: docData.fiscal_year,
        posting_date: docData.posting_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        document_date: docData.document_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        total_amount: docData.total_amount,
        currency: docData.currency || 'USD',
        reason: docData.reason || '',
        header_text: docData.header_text || '',
        line_items: docData.line_items?.map(item => ({
          ...item,
          wbs_name: item.wbs_element_name || item.wbs_element_id
        })) || []
      });
      await fetchWBSElements(docData.project_id);
      if (docData.budget_type !== 'ORIGINAL') {
        await fetchWBSBudgets(docData.project_id, docData.fiscal_year);
      }
      setShowDialog(true);
    } catch (error) {
      toast.error('Failed to load document for editing');
    }
  };

  const handleUpdate = async () => {
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.map(({ wbs_name, ...item }) => item)
      };

      await psAPI.updateBudgetDocument(selectedDoc.document_number, payload);
      toast.success('Budget document updated successfully');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'object' ? errorMsg.msg : errorMsg || 'Failed to update document');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    const config = MODE_CONFIG[budgetMode];
    setFormData(prev => ({ ...prev, budget_type: config.defaultType }));
    setShowDialog(true);
  };

  const resetForm = () => {
    setIsEditing(false);
    setFormData({
      project_id: '', budget_type: MODE_CONFIG[budgetMode].defaultType, fiscal_year: new Date().getFullYear(),
      posting_date: new Date().toISOString().split('T')[0], document_date: new Date().toISOString().split('T')[0],
      total_amount: 0, currency: 'USD', reason: '', header_text: '', line_items: []
    });
    setWbsElements([]);
    setWbsBudgets([]);
  };

  const formatCurrency = (value, currency = 'USD') => {
    const localeMap = {
      'USD': 'en-US', 'EUR': 'de-DE', 'GBP': 'en-GB', 'INR': 'en-IN',
      'JPY': 'ja-JP', 'AUD': 'en-AU', 'CAD': 'en-CA', 'CHF': 'de-CH',
      'CNY': 'zh-CN', 'SGD': 'en-SG'
    };
    return new Intl.NumberFormat(localeMap[currency] || 'en-US', {
      style: 'currency', currency: currency || 'USD', minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getBudgetTypeIcon = (type) => {
    const icons = {
      'ORIGINAL': <DollarSign className="h-4 w-4" />,
      'SUPPLEMENT': <ArrowUpRight className="h-4 w-4" />,
      'RETURN': <ArrowDownRight className="h-4 w-4" />,
      'TRANSFER': <ArrowLeftRight className="h-4 w-4" />
    };
    return icons[type] || <DollarSign className="h-4 w-4" />;
  };

  const getStatusBadge = (status) => {
    const config = {
      'NOT_RELEASED': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      'PARTIALLY_RELEASED': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      'RELEASED': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' }
    };
    const c = config[status] || config['NOT_RELEASED'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  // Filter documents by current mode
  const modeTypes = MODE_CONFIG[budgetMode].types;
  const modeFilteredDocs = documents.filter(d => modeTypes.includes(d.budget_type));

  // Calculate stats for current mode
  const stats = {
    total: modeFilteredDocs.length,
    totalAmount: modeFilteredDocs.reduce((sum, d) => sum + (d.total_amount || 0), 0),
    released: modeFilteredDocs.filter(d => d.release_status === 'RELEASED').length,
    pending: modeFilteredDocs.filter(d => d.release_status !== 'RELEASED').length
  };

  const filteredDocs = modeFilteredDocs.filter(d =>
    d.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.project_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const modeConfig = MODE_CONFIG[budgetMode];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="budget-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ps/planning')} className="hover:bg-slate-100" data-testid="back-button">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-lg">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Budget Management</h1>
                <p className="text-sm text-slate-500">Create and manage project budgets</p>
              </div>
            </div>
          </div>
          {writable && (
          <Button onClick={openCreateDialog} className="bg-orange-600 hover:bg-orange-700" data-testid="new-budget-btn">
            <Plus className="h-4 w-4 mr-2" />
            {modeConfig.label === 'Original Budget' ? 'New Budget Document' : `New ${modeConfig.label}`}
          </Button>
          )}
        </div>

        {/* Mode Selector */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit" data-testid="budget-mode-selector">
          {Object.entries(MODE_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            const isActive = budgetMode === key;
            return (
              <button
                key={key}
                data-testid={`mode-${key}`}
                onClick={() => setBudgetMode(key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {config.label}
              </button>
            );
          })}
        </div>

        {/* Mode Description */}
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          budgetMode === 'original' ? 'bg-orange-50 border-orange-200' :
          budgetMode === 'change' ? 'bg-amber-50 border-amber-200' :
          'bg-orange-50 border-orange-200'
        }`} data-testid="mode-description">
          <modeConfig.icon className={`h-5 w-5 ${
            budgetMode === 'original' ? 'text-orange-600' :
            budgetMode === 'change' ? 'text-amber-600' :
            'text-orange-600'
          }`} />
          <p className={`text-sm ${
            budgetMode === 'original' ? 'text-orange-700' :
            budgetMode === 'change' ? 'text-amber-700' :
            'text-orange-700'
          }`}>{modeConfig.description}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Documents</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Total Amount</p>
                  <p className="text-2xl font-bold text-orange-700" data-testid="stat-amount">{formatCurrency(stats.totalAmount)}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-600">Released</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="stat-released">{stats.released}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-600">Pending Release</p>
                  <p className="text-2xl font-bold text-amber-700" data-testid="stat-pending">{stats.pending}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search by document number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="search-input" />
              </div>
              <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({...filters, project_id: v === "all" ? "" : v})}>
                <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200" data-testid="filter-project">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {budgetMode === 'change' && (
                <Select value={filters.budget_type || "all"} onValueChange={(v) => setFilters({...filters, budget_type: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-[160px] bg-slate-50 border-slate-200" data-testid="filter-change-type">
                    <SelectValue placeholder="All Changes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Changes</SelectItem>
                    <SelectItem value="SUPPLEMENT">Supplement</SelectItem>
                    <SelectItem value="RETURN">Return</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Select value={String(filters.fiscal_year)} onValueChange={(v) => setFilters({...filters, fiscal_year: parseInt(v)})}>
                <SelectTrigger className="w-[120px] bg-slate-50 border-slate-200" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Document #</TableHead>
                <TableHead className="font-semibold text-slate-600">Type</TableHead>
                <TableHead className="font-semibold text-slate-600">Project</TableHead>
                <TableHead className="font-semibold text-slate-600">FY</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Amount</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Released</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2 text-slate-500">
                    <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>
                    Loading...
                  </div>
                </TableCell></TableRow>
              ) : filteredDocs.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <Wallet className="h-10 w-10 text-slate-300" />
                    <p className="text-slate-500">
                      {budgetMode === 'original' ? 'No original budget documents found' :
                       budgetMode === 'change' ? 'No budget change documents found. Create a supplement or return.' :
                       'No budget transfer documents found'}
                    </p>
                    <Button variant="outline" size="sm" onClick={openCreateDialog} className="mt-2" data-testid="empty-state-create-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Create {modeConfig.label}
                    </Button>
                  </div>
                </TableCell></TableRow>
              ) : filteredDocs.map((doc) => (
                <TableRow key={doc.document_number} className="group hover:bg-orange-50/50 transition-colors" data-testid={`budget-doc-row-${doc.document_number}`}>
                  <TableCell>
                    <span className="font-mono text-sm font-medium text-orange-600">{doc.document_number}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getBudgetTypeIcon(doc.budget_type)}
                      <span className="text-sm">{doc.budget_type}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{doc.project_name || doc.project_id}</TableCell>
                  <TableCell className="text-slate-600">{doc.fiscal_year}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(doc.total_amount, doc.currency)}</TableCell>
                  <TableCell className="text-right font-medium text-emerald-600">{formatCurrency(doc.released_amount, doc.currency)}</TableCell>
                  <TableCell>{getStatusBadge(doc.release_status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openViewDialog(doc)} title="View" data-testid={`view-btn-${doc.document_number}`}>
                        <Eye className="h-4 w-4 text-slate-600" />
                      </Button>
                      {writable && doc.release_status === 'NOT_RELEASED' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openEditDialog(doc)} title="Edit" data-testid={`edit-btn-${doc.document_number}`}>
                          <Edit className="h-4 w-4 text-slate-600" />
                        </Button>
                      )}
                      {writable && doc.release_status !== 'RELEASED' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-emerald-100" onClick={() => { setSelectedDoc(doc); setShowReleaseDialog(true); }} title="Release" data-testid={`release-btn-${doc.document_number}`}>
                          <Send className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredDocs.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-500">Showing <span className="font-medium text-slate-700">{filteredDocs.length}</span> documents</p>
            </div>
          )}
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold" data-testid="dialog-title">
                {isEditing ? `Edit Budget Document: ${selectedDoc?.document_number}` : `Create ${modeConfig.label} Document`}
              </DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="header" className="space-y-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="header" data-testid="tab-header">Header Data</TabsTrigger>
                <TabsTrigger value="items" data-testid="tab-items">Line Items ({formData.line_items.length})</TabsTrigger>
                {(budgetMode === 'change' || budgetMode === 'transfer') && formData.project_id && (
                  <TabsTrigger value="current-budget" data-testid="tab-current-budget">Current Budget</TabsTrigger>
                )}
              </TabsList>
              
              <TabsContent value="header" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Project <span className="text-red-500">*</span></Label>
                    <Select value={formData.project_id || "__none__"} onValueChange={(v) => handleProjectChange(v === "__none__" ? "" : v)}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select project</SelectItem>
                        {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Budget Type <span className="text-red-500">*</span></Label>
                    {budgetMode === 'change' ? (
                      <Select value={formData.budget_type} onValueChange={(v) => setFormData(prev => ({ ...prev, budget_type: v }))}>
                        <SelectTrigger className="bg-slate-50" data-testid="form-budget-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SUPPLEMENT">Budget Supplement (Increase)</SelectItem>
                          <SelectItem value="RETURN">Budget Return (Decrease)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className={`flex items-center gap-2 h-10 px-3 rounded-md border bg-slate-100 text-sm ${
                        budgetMode === 'original' ? 'border-orange-200 text-orange-700' :
                        'border-orange-200 text-orange-700'
                      }`} data-testid="form-budget-type-display">
                        {getBudgetTypeIcon(formData.budget_type)}
                        <span className="font-medium">{formData.budget_type === 'ORIGINAL' ? 'Original Budget' : 'Budget Transfer'}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Fiscal Year</Label>
                    <Select value={String(formData.fiscal_year)} onValueChange={(v) => setFormData(prev => ({ ...prev, fiscal_year: parseInt(v) }))}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-fiscal-year"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                        <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                        <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                        <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                        <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                        <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                        <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Posting Date</Label>
                    <Input type="date" value={formData.posting_date} onChange={(e) => setFormData(prev => ({ ...prev, posting_date: e.target.value }))} className="bg-slate-50" data-testid="form-posting-date" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Document Date</Label>
                    <Input type="date" value={formData.document_date} onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))} className="bg-slate-50" data-testid="form-document-date" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium">
                      Reason {budgetMode !== 'original' && <span className="text-red-500">*</span>}
                    </Label>
                    <Textarea value={formData.reason} onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))} placeholder={
                      budgetMode === 'change' ? 'Explain the reason for budget change...' :
                      budgetMode === 'transfer' ? 'Explain why budget is being transferred...' :
                      'Budget request reason...'
                    } className="bg-slate-50" data-testid="form-reason" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="items" className="space-y-4">
                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className={`grid gap-2 items-end mb-4 ${formData.budget_type === 'TRANSFER' ? 'grid-cols-4' : 'grid-cols-3'}`}>
                      <div className="space-y-1">
                        <Label className="text-xs">{formData.budget_type === 'TRANSFER' ? 'Source WBS Element' : 'WBS Element'}</Label>
                        <Select value={lineItem.wbs_element_id || "__none__"} onValueChange={(v) => setLineItem(prev => ({ ...prev, wbs_element_id: v === "__none__" ? "" : v }))}>
                          <SelectTrigger className="h-9 bg-slate-50" data-testid="line-wbs"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Select WBS</SelectItem>
                            {wbsElements.map(w => {
                              const budget = wbsBudgets.find(b => b.wbs_element_id === w.wbs_element_id);
                              return (
                                <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>
                                  {w.wbs_element_id} - {w.name}
                                  {budget && budgetMode !== 'original' ? ` (Budget: ${formatCurrency(budget.current_budget)})` : ''}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.budget_type === 'TRANSFER' && (
                        <div className="space-y-1">
                          <Label className="text-xs">Target WBS</Label>
                          <Select value={lineItem.target_wbs_element_id || "__none__"} onValueChange={(v) => setLineItem(prev => ({ ...prev, target_wbs_element_id: v === "__none__" ? "" : v }))}>
                            <SelectTrigger className="h-9 bg-slate-50" data-testid="line-target-wbs"><SelectValue placeholder="Target" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Select Target</SelectItem>
                              {wbsElements.filter(w => w.wbs_element_id !== lineItem.wbs_element_id).map(w => (
                                <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.wbs_element_id} - {w.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Amount</Label>
                        <Input type="number" className="h-9 bg-slate-50" placeholder="0.00" value={lineItem.amount || ''} onChange={(e) => setLineItem(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))} data-testid="line-amount" />
                      </div>
                      <Button onClick={addLineItem} size="sm" className="h-9 bg-orange-600 hover:bg-orange-700" data-testid="add-line-item-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {formData.line_items.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">WBS Element</TableHead>
                            {formData.budget_type === 'TRANSFER' && <TableHead className="text-xs">Target WBS</TableHead>}
                            <TableHead className="text-xs text-right">Amount</TableHead>
                            <TableHead className="text-xs w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.line_items.map((item, index) => (
                            <TableRow key={item._uid || item.cost_element_id || `row-${index}`}>
                              <TableCell className="text-sm">{item.wbs_name || item.wbs_element_id}</TableCell>
                              {formData.budget_type === 'TRANSFER' && <TableCell className="text-sm">{item.target_wbs_element_id}</TableCell>}
                              <TableCell className="text-sm text-right font-medium">{formatCurrency(item.amount, formData.currency)}</TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => removeLineItem(index)} data-testid={`remove-line-${index}`}><Trash2 className="h-3 w-3" /></Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-orange-50">
                            <TableCell colSpan={formData.budget_type === 'TRANSFER' ? 2 : 1} className="font-semibold">Total</TableCell>
                            <TableCell className="text-right font-bold text-orange-600" data-testid="line-items-total">{formatCurrency(formData.total_amount, formData.currency)}</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-slate-500">No line items added yet</div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Current Budget Tab - for change and transfer modes */}
              {(budgetMode === 'change' || budgetMode === 'transfer') && formData.project_id && (
                <TabsContent value="current-budget" className="space-y-4">
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-slate-600" />
                        Current Budget Status for Selected Project
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {wbsBudgets.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs font-semibold">WBS Element</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Original</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Supplements</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Returns</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Current Budget</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Actual Costs</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Available</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {wbsBudgets.map((wb) => (
                              <TableRow key={wb.wbs_element_id}>
                                <TableCell className="text-sm">
                                  <div>
                                    <span className="font-medium">{wb.wbs_element_id}</span>
                                    {wb.wbs_element_name && <span className="text-slate-500 ml-1">- {wb.wbs_element_name}</span>}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-right">{formatCurrency(wb.original_budget)}</TableCell>
                                <TableCell className="text-sm text-right text-green-600">{formatCurrency(wb.supplements)}</TableCell>
                                <TableCell className="text-sm text-right text-red-600">{formatCurrency(wb.returns)}</TableCell>
                                <TableCell className="text-sm text-right font-semibold">{formatCurrency(wb.current_budget)}</TableCell>
                                <TableCell className="text-sm text-right">{formatCurrency(wb.actual_costs)}</TableCell>
                                <TableCell className={`text-sm text-right font-medium ${wb.available_budget < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {formatCurrency(wb.available_budget)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          No budget data found for this project. Create an original budget first.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
            </Tabs>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="dialog-cancel-btn">Cancel</Button>
              <Button onClick={isEditing ? handleUpdate : handleSubmit} className="bg-orange-600 hover:bg-orange-700" data-testid="dialog-submit-btn">{isEditing ? 'Update' : 'Create'} Document</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg font-semibold">Budget Document: {selectedDoc?.document_number}</DialogTitle>
                {selectedDoc && getStatusBadge(selectedDoc.release_status)}
              </div>
            </DialogHeader>
            {selectedDoc && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div><p className="text-xs text-slate-500">Project</p><p className="font-medium">{selectedDoc.project_name || selectedDoc.project_id}</p></div>
                  <div><p className="text-xs text-slate-500">Type</p><p className="font-medium">{selectedDoc.budget_type}</p></div>
                  <div><p className="text-xs text-slate-500">Fiscal Year</p><p className="font-medium">{selectedDoc.fiscal_year}</p></div>
                  <div><p className="text-xs text-slate-500">Total Amount</p><p className="font-bold text-orange-600">{formatCurrency(selectedDoc.total_amount, selectedDoc.currency)}</p></div>
                  <div><p className="text-xs text-slate-500">Released Amount</p><p className="font-bold text-emerald-600">{formatCurrency(selectedDoc.released_amount, selectedDoc.currency)}</p></div>
                  <div><p className="text-xs text-slate-500">Posting Date</p><p className="font-medium">{selectedDoc.posting_date}</p></div>
                </div>
                {selectedDoc.reason && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Reason</p>
                    <p className="text-sm text-amber-800">{selectedDoc.reason}</p>
                  </div>
                )}
                {selectedDoc.line_items?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Line Items</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>WBS Element</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDoc.line_items.map((item, index) => (
                          <TableRow key={item._uid || item.cost_element_id || `view-${index}`}>
                            <TableCell>{item.wbs_element_id}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.amount, selectedDoc.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              {writable && selectedDoc?.release_status === 'NOT_RELEASED' && (
                <Button onClick={() => { setShowViewDialog(false); openEditDialog(selectedDoc); }} className="bg-orange-600 hover:bg-orange-700" data-testid="view-edit-btn">
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              )}
              {writable && selectedDoc?.release_status !== 'RELEASED' && (
                <Button onClick={() => { setShowViewDialog(false); setShowReleaseDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="view-release-btn">
                  <Send className="h-4 w-4 mr-2" /> Release
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowViewDialog(false)} data-testid="view-close-btn">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Release Dialog */}
        <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Release Budget Document</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600 mb-4">
                Release budget document <span className="font-semibold">{selectedDoc?.document_number}</span>?
              </p>
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-orange-700">Amount to Release</span>
                  <span className="font-bold text-orange-700">{formatCurrency(selectedDoc?.total_amount - (selectedDoc?.released_amount || 0), selectedDoc?.currency)}</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReleaseDialog(false)} data-testid="release-cancel-btn">Cancel</Button>
              <Button onClick={() => handleRelease()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="release-confirm-btn">
                <CheckCircle className="h-4 w-4 mr-2" /> Release
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BudgetManagement;
